import React, { createContext, useState, useContext, ReactNode } from 'react';
import { CashReconciliationDto } from '../../../app/services/api/cashTransactions';

interface CashReconciliationContextType {
  // Active reconciliation session
  activeReconciliation: CashReconciliationDto | null;
  setActiveReconciliation: (reconciliation: CashReconciliationDto | null) => void;

  // Count method toggle
  useQuickTotal: boolean;
  setUseQuickTotal: (useQuick: boolean) => void;

  // Till selection
  selectedTillNumber: string | null;
  setSelectedTillNumber: (tillNumber: string | null) => void;

  // Variance management
  hasVariance: boolean;
  varianceNeedsApproval: boolean;
  setVarianceNeedsApproval: (needsApproval: boolean) => void;

  // Investigation mode
  isInvestigating: boolean;
  setIsInvestigating: (investigating: boolean) => void;

  // Save state
  isSaving: boolean;
  setIsSaving: (saving: boolean) => void;

  // Modified flag
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
}

const CashReconciliationContext = createContext<CashReconciliationContextType | undefined>(undefined);

export const CashReconciliationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [activeReconciliation, setActiveReconciliation] = useState<CashReconciliationDto | null>(null);
  const [useQuickTotal, setUseQuickTotal] = useState<boolean>(true);
  const [selectedTillNumber, setSelectedTillNumber] = useState<string | null>(null);
  const [varianceNeedsApproval, setVarianceNeedsApproval] = useState<boolean>(false);
  const [isInvestigating, setIsInvestigating] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  const hasVariance = activeReconciliation ? activeReconciliation.variance !== 0 : false;

  const value: CashReconciliationContextType = {
    activeReconciliation,
    setActiveReconciliation,
    useQuickTotal,
    setUseQuickTotal,
    selectedTillNumber,
    setSelectedTillNumber,
    hasVariance,
    varianceNeedsApproval,
    setVarianceNeedsApproval,
    isInvestigating,
    setIsInvestigating,
    isSaving,
    setIsSaving,
    hasUnsavedChanges,
    setHasUnsavedChanges,
  };

  return (
    <CashReconciliationContext.Provider value={value}>
      {children}
    </CashReconciliationContext.Provider>
  );
};

export const useCashReconciliation = (): CashReconciliationContextType => {
  const context = useContext(CashReconciliationContext);
  if (!context) {
    throw new Error('useCashReconciliation must be used within CashReconciliationProvider');
  }
  return context;
};
